// api.js - Minimal API helpers for admin RBAC UI
// Uses window.fetchWithAuth for JWT auth (provided by App.js AuthProvider)

import { API_URL } from "./config";

export async function fetchUsers(fetchWithAuth) {
  const res = await fetchWithAuth(`${API_URL}/users/`);
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
}

// ---- Candidates ----
export async function fetchCandidates(fetchWithAuth) {
  const res = await fetchWithAuth(`${API_URL}/candidates/`);
  if (!res.ok) throw new Error("Failed to fetch candidates");
  return res.json();
}

/**
 * Search candidates with pagination and filtering
 * @param {Function} fetchWithAuth - Auth function for API calls
 * @param {Object} options - Search options
 * @param {string} options.search - Search term for filtering candidates
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.limit - Number of results per page
 * @returns {Promise<{data: Array, total: number, page: number, pages: number}>}
 */
export async function searchCandidates(fetchWithAuth, { search = '', page = 1, limit = 20 } = {}) {
  const queryParams = new URLSearchParams({
    search: search,
    page: page,
    limit: limit
  }).toString();
  
  const res = await fetchWithAuth(`${API_URL}/candidates/search?${queryParams}`);
  if (!res.ok) throw new Error("Failed to search candidates");
  return res.json();
}

export async function createCandidate(fetchWithAuth, data) {
  const res = await fetchWithAuth(`${API_URL}/candidates/`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to create candidate");
  return res.json();
}

export async function updateCandidate(fetchWithAuth, candidateId, data) {
  const res = await fetchWithAuth(`${API_URL}/candidates/${candidateId}`, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  
  if (!res.ok) {
    // Try to get more detailed error message from the response
    try {
      const errorData = await res.json();
      throw new Error(errorData.detail || "Failed to update candidate");
    } catch (e) {
      throw new Error("Failed to update candidate");
    }
  }
  
  return res.json();
}

export async function deleteCandidate(fetchWithAuth, candidateId) {
  const res = await fetchWithAuth(`${API_URL}/candidates/${candidateId}`, {
    method: "DELETE"
  });
  
  if (!res.ok) {
    if (res.status === 409) {
      throw new Error("Cannot delete candidate with associated records. Please deactivate instead.");
    }
    throw new Error("Failed to delete candidate");
  }

  if (res.status == 204) return {};
  
  return res.json();
}

export async function resetCandidatePassword(fetchWithAuth, candidateId, password) {
  const res = await fetchWithAuth(`${API_URL}/candidates/${candidateId}/reset-password`, {
    method: "POST",
    body: JSON.stringify({ password }),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to reset candidate password");
  return res.json();
}

export async function fetchCandidateScheduleContext(fetchWithAuth, candidateId) {
  const res = await fetchWithAuth(
    `${API_URL}/candidates/${candidateId}/schedule-context`
  );

  if (!res.ok) {
    let message = "Failed to fetch candidate schedule context";

    try {
      const err = await res.json();
      message = err.detail || message;
    } catch {
      // Ignore JSON parse errors
    }

    throw new Error(message);
  }

  return res.json();
}

export async function createEvent(fetchWithAuth, data) {
  const res = await fetchWithAuth(`${API_URL}/events/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!res.ok) {
    let message = "Failed to schedule event";

    try {
      const err = await res.json();
      message = err.detail || message;
    } catch {
      // Ignore JSON parse errors
    }

    throw new Error(message);
  }

  return res.json();
}



/**
 * Creates a new user in the system
 * @param {Function} fetchWithAuth - Authenticated fetch function
 * @param {Object} data - User data to create
 * @returns {Promise<Object>} Created user data
 * @throws {Error} If user creation fails
 */
export async function createUser(fetchWithAuth, data) {
  const res = await fetchWithAuth(`${API_URL}/users/`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  });

  if (!res.ok) {
    try {
      const responseText = await res.text();
      const errorData = safeJsonParse(responseText);
      
      // Handle error format: {"error": {"code": 400, "message": "..."}}
      if (errorData.error?.message) {
        throw new Error(errorData.error.message);
      }

      // Handle validation errors (status 422 for Pydantic validation)
      if (res.status === 422 && errorData.detail) {
        throw new Error(formatValidationError(errorData.detail));
      }

      // Handle specific HTTP status codes
      const errorMessage = getErrorMessageForStatus(res.status, errorData);
      throw new Error(errorMessage);
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Failed to process server response');
      }
      throw error;
    }
  }

  return parseJsonResponse(res);
}

/**
 * Safely parses JSON response, returns null if parsing fails
 * @param {string} text - JSON string to parse
 * @returns {Object|null} Parsed object or null
 */
function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

/**
 * Formats validation error details into a user-friendly message
 * @param {Object|Array} detail - Error details from API
 * @returns {string} Formatted error message
 */
function formatValidationError(detail) {
  if (Array.isArray(detail)) {
    return detail.map(err => 
      `${err.loc?.[err.loc.length - 1] || 'field'}: ${err.msg || 'Invalid value'}`
    ).join('; ');
  }
  return String(detail);
}

/**
 * Gets appropriate error message for HTTP status code
 * @param {number} status - HTTP status code
 * @param {Object} errorData - Error response data
 * @returns {string} Error message
 */
function getErrorMessageForStatus(status, errorData) {
  const statusHandlers = {
    400: () => 
      errorData.detail?.includes('already registered') || 
      errorData.error?.message?.includes('already registered')
        ? 'A user with this email already exists.'
        : errorData.detail || errorData.message || 'Invalid input provided',
    
    403: () => 'You do not have permission to create users.',
    
    409: () => 'A user with this email already exists.',
    
    default: () => errorData.detail || errorData.message || `Request failed with status ${status}`
  };

  const handler = statusHandlers[status] || statusHandlers.default;
  return handler();
}

/**
 * Parses JSON response or throws appropriate error
 * @param {Response} response - Fetch response object
 * @returns {Promise<Object>} Parsed JSON response
 */
async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    throw new Error('Failed to process server response');
  }
}

export async function updateUserRole(fetchWithAuth, userId, roleId) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/role`, {
    method: "PATCH",
    body: JSON.stringify({ role_id: roleId }),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to update user role");
  return res.json();
}

// Update user: expects full user object, with role_ids as an array of UUIDs
export async function updateUser(fetchWithAuth, userId, data) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}`, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to update user");
  return res.json();
}

export async function deleteUser(fetchWithAuth, userId) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete user");
}

export async function resetUserPassword(fetchWithAuth, userId) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/reset-password`, {
    method: "POST"
  });
  if (!res.ok) throw new Error("Failed to reset password and send email");
  return res.json();
}

// ---- Roles ----
export async function fetchRoles(fetchWithAuth, page = 0, limit = 10) {
  const offset = page * limit;
  const url = new URL(`${API_URL}/roles/`);
  url.searchParams.append('offset', offset);
  url.searchParams.append('limit', limit);
  
  const res = await fetchWithAuth(url.toString());
  if (!res.ok) throw new Error("Failed to fetch roles");
  return res.json();
}

export async function createRole(fetchWithAuth, data) {
  const res = await fetchWithAuth(`${API_URL}/roles/`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to create role");
  return res.json();
}

export async function updateRole(fetchWithAuth, roleId, data) {
  const res = await fetchWithAuth(`${API_URL}/roles/${roleId}`, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to update role");
  return res.json();
}

export async function deleteRole(fetchWithAuth, roleId) {
  const res = await fetchWithAuth(`${API_URL}/roles/${roleId}`, {
    method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete role");
}

export async function assignPermissionsToRole(fetchWithAuth, roleId, permissionIds) {
  const res = await fetchWithAuth(`${API_URL}/roles/${roleId}/permissions`, {
    method: "POST",
    body: JSON.stringify(permissionIds), // Send as raw array
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to assign permissions");
  return res.json();
}

// ---- Permissions ----
// Fetch all available permissions (for admin roles UI)
export async function fetchPermissions(fetchWithAuth) {
  // Note: Removed trailing slash for better compatibility
  const res = await fetchWithAuth(`${API_URL}/roles/permissions`);
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || "Failed to fetch permissions");
  }
  return res.json();
}

// Fetch permissions for a specific role
export async function fetchRolePermissions(fetchWithAuth, roleId) {
  const res = await fetchWithAuth(`${API_URL}/roles/${roleId}/permissions`);
  if (!res.ok) throw new Error("Failed to fetch role permissions");
  return res.json();
}

export async function createPermission(fetchWithAuth, data) {
  const res = await fetchWithAuth(`${API_URL}/permissions/`, {
    method: "POST",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to create permission");
  return res.json();
}

export async function updatePermission(fetchWithAuth, permissionId, data) {
  const res = await fetchWithAuth(`${API_URL}/permissions/${permissionId}`, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to update permission");
  return res.json();
}

export async function deletePermission(fetchWithAuth, permissionId) {
  const res = await fetchWithAuth(`${API_URL}/permissions/${permissionId}`, {
    method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete permission");
}

export async function assignPermissionToUser(fetchWithAuth, userId, permission) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/permissions`, {
    method: "POST",
    body: JSON.stringify({ permission }),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to assign permission to user");
  return res.json();
}

export async function revokePermissionFromUser(fetchWithAuth, userId, permission) {
  const res = await fetchWithAuth(`${API_URL}/users/${userId}/permissions`, {
    method: "DELETE",
    body: JSON.stringify({ permission }),
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error("Failed to revoke permission from user");
  return res.json();
}
